import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';

interface AnalyticsRow {
  country: string | null;
  city: string | null;
  visit_duration_seconds: number | null;
}

interface Props {
  analytics: AnalyticsRow[];
}

interface CityData {
  name: string;
  visits: number;
  avgDuration: number;
}

interface CountryData {
  name: string;
  visits: number;
  avgDuration: number;
  cities: CityData[];
}

const formatDur = (s: number) => {
  if (s === 0) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
};

const GeoEngagement = ({ analytics }: Props) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const countries = useMemo(() => {
    const map: Record<string, { visits: number; totalDur: number; durCount: number; cities: Record<string, { visits: number; totalDur: number; durCount: number }> }> = {};

    analytics.forEach((r) => {
      const country = r.country || 'Unknown';
      if (!map[country]) map[country] = { visits: 0, totalDur: 0, durCount: 0, cities: {} };
      map[country].visits += 1;
      if (r.visit_duration_seconds && r.visit_duration_seconds > 0) {
        map[country].totalDur += r.visit_duration_seconds;
        map[country].durCount += 1;
      }

      const city = r.city || 'Unknown';
      if (!map[country].cities[city]) map[country].cities[city] = { visits: 0, totalDur: 0, durCount: 0 };
      map[country].cities[city].visits += 1;
      if (r.visit_duration_seconds && r.visit_duration_seconds > 0) {
        map[country].cities[city].totalDur += r.visit_duration_seconds;
        map[country].cities[city].durCount += 1;
      }
    });

    return Object.entries(map)
      .sort((a, b) => b[1].visits - a[1].visits)
      .map(([name, d]): CountryData => ({
        name,
        visits: d.visits,
        avgDuration: d.durCount > 0 ? Math.round(d.totalDur / d.durCount) : 0,
        cities: Object.entries(d.cities)
          .sort((a, b) => b[1].visits - a[1].visits)
          .map(([cName, cd]): CityData => ({
            name: cName,
            visits: cd.visits,
            avgDuration: cd.durCount > 0 ? Math.round(cd.totalDur / cd.durCount) : 0,
          })),
      }));
  }, [analytics]);

  const maxVisits = countries[0]?.visits || 1;

  const toggle = (country: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country);
      else next.add(country);
      return next;
    });
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
          <MapPin className="w-4 h-4" /> Geographic Engagement
        </CardTitle>
      </CardHeader>
      <CardContent>
        {countries.some(c => c.name === 'Unknown') && (
          <p className="text-xs text-muted-foreground mb-3 px-1 py-2 rounded" style={{ background: 'var(--blue-light)' }}>
            ℹ️ "Unknown" entries are from visitors whose geo-lookup failed (previously rate-limited). New visits now use Cloudflare headers for reliable country detection.
          </p>
        )}
        {countries.length === 0 ? (
          <p className="text-muted-foreground text-sm">No geographic data yet</p>
        ) : (
          <div className="space-y-1">
            {/* Header */}
            <div className="grid grid-cols-[1fr_80px_100px] gap-2 text-xs text-muted-foreground pb-2 border-b" style={{ borderColor: 'var(--line-slate)' }}>
              <span>Location</span>
              <span className="text-right">Visits</span>
              <span className="text-right">Avg Duration</span>
            </div>

            {countries.map((c) => (
              <div key={c.name}>
                {/* Country row */}
                <button
                  onClick={() => toggle(c.name)}
                  className="w-full grid grid-cols-[1fr_80px_100px] gap-2 items-center py-2 text-sm hover:bg-muted/50 rounded px-1 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {expanded.has(c.name) ? (
                      <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate font-medium" style={{ color: 'var(--ink)' }}>{c.name}</span>
                    <div className="flex-1 h-1.5 rounded-full ml-2" style={{ background: 'var(--line-slate)' }}>
                      <div className="h-full rounded-full" style={{ width: `${(c.visits / maxVisits) * 100}%`, background: 'var(--blue)' }} />
                    </div>
                  </div>
                  <span className="text-right font-medium" style={{ color: 'var(--ink)' }}>{c.visits}</span>
                  <span className="text-right text-muted-foreground">{formatDur(c.avgDuration)}</span>
                </button>

                {/* City rows */}
                {expanded.has(c.name) && c.cities.length > 0 && (
                  <div className="ml-6 border-l-2 pl-3 mb-2" style={{ borderColor: 'var(--line-slate)' }}>
                    {c.cities.map((city) => (
                      <div key={city.name} className="grid grid-cols-[1fr_80px_100px] gap-2 py-1.5 text-xs">
                        <span className="truncate text-muted-foreground">{city.name}</span>
                        <span className="text-right" style={{ color: 'var(--ink)' }}>{city.visits}</span>
                        <span className="text-right text-muted-foreground">{formatDur(city.avgDuration)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GeoEngagement;
